/**
 * Auth State
 *
 * Centralized state for authentication.
 * Uses Signal.State for reactive updates.
 *
 * This state class will eventually replace the legacy appContext singleton.
 * During migration, both will coexist with the auth service bridging between them.
 */

import { Signal } from "@lit-labs/signals";
import type { IAuthState, IWritableAuthState, ICurrentUser } from "./types";

export class AuthState implements IAuthState, IWritableAuthState {
  /** Current authenticated user */
  readonly currentUser = new Signal.State<ICurrentUser | undefined>(undefined);

  /** Current access token */
  readonly accessToken = new Signal.State<string | undefined>(undefined);

  /** Current public profile handle being viewed */
  readonly currentPublicProfile = new Signal.State<string | undefined>(undefined);

  /** Computed: true if user is authenticated */
  readonly isAuthenticated = new Signal.Computed(() => this.currentUser.get() !== undefined);

  /** Computed: true if user has superuser role */
  readonly isSuperuser = new Signal.Computed(
    () => this.currentUser.get()?.roles.some((role) => role === "superuser") ?? false,
  );

  /** Promise that resolves when initial auth check completes */
  readonly ready: Promise<void>;

  /** Internal resolver for the ready promise */
  private _readyResolve!: () => void;

  /** Track if ready has been called */
  private _isReady = false;

  constructor() {
    this.ready = new Promise<void>((resolve) => {
      this._readyResolve = resolve;
    });
  }

  /** Mark auth state as ready (initial auth check complete) */
  setReady(): void {
    if (!this._isReady) {
      this._isReady = true;
      this._readyResolve();
    }
  }
}
